import React, { useState, useEffect } from 'react'
import { Button, Card, Col, Space, Steps, message, theme } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle, faChevronLeft, faChevronRight, faXmarkCircle } from '@fortawesome/free-solid-svg-icons'
import { useDispatch } from 'react-redux'
import CustomRow from '../../../reusable-components/CustomRow'
import { handleError } from '../../../lib/utils'
import coreReducer from '../../../../core/utils/reducer'
import { createCarePlanTemplate, updateCarePlanTemplate } from '../../utils/utils'
import GeneralForm from '../../Common/WizardSteps/GeneralForm'
import DataTrackerForm from '../../Common/WizardSteps/DataTrackerForm'
import VisitTrackers from '../../Common/WizardSteps/VisitTrackers'
import Summary from '../../Common/WizardSteps/Summary'
import ImmunizationsManagement from './ImmunizationsManagement'
import LifestyleRecommendations from './LifestyleRecommendations'
import RiskFactorAssessment from './RiskFactorAssessment'
import EducationalResources from './EducationalResources'

// Import styles
import '../styles/PlanTemplateForm.css'

const PlanTemplateForm = ({
  onSave,
  initialValues,
  isLoading,
  isEditing = false,
  refreshView,
  recordData,
  isNew = true,
  planType = 'preventive',
  planLabel = 'Preventive Care Plan',
  dataPoints = []
}) => {
  // State for form data
  const [currentStep, setCurrentStep] = useState(0)
  const [general, setGeneral] = useState({
    name: recordData?.name || '',
    description: recordData?.description || ''
  })
  const [data, setData] = useState(isNew ? [] : recordData?.tracking?.filter((i) => i.trackerType === 'data') || [])
  const [visits, setVisits] = useState(
    isNew ? [] : recordData?.tracking?.filter((i) => i.trackerType === 'visits') || []
  )
  const [immunizations, setImmunizations] = useState(recordData?.immunizations || [])
  const [lifestyle, setLifestyle] = useState(recordData?.lifestyle || [])
  const [riskFactors, setRiskFactors] = useState(recordData?.riskFactors || [])
  const [education, setEducation] = useState(recordData?.education || [])

  const dispatch = useDispatch()
  const { token } = theme.useToken()

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const handleAddTracking = (key, selection) => {
    let tmpData = []
    let tmpVisits = []
    const parsedSelection = JSON.parse(selection)

    switch (key) {
      case 'data':
        tmpData = JSON.parse(JSON.stringify(data))
        tmpData.push({
          dataLabel: parsedSelection.label,
          dataKey: parsedSelection.key,
          count: 1,
          trackerType: 'data',
          intervalDays: 180
        })
        setData(tmpData)
        break
      case 'visits':
        tmpVisits = JSON.parse(JSON.stringify(visits))
        tmpVisits.push({
          dataLabel: parsedSelection.label,
          dataKey: parsedSelection.key,
          count: 1,
          trackerType: 'visits',
          intervalDays: 180
        })
        setVisits(tmpVisits)
        break
      default:
        break
    }
  }

  const handleUpdateTracker = (value, index, property, key) => {
    let tmpData = []
    let tmpVisits = []

    switch (key) {
      case 'data':
        tmpData = JSON.parse(JSON.stringify(data))
        tmpData[index][property] = value
        setData(tmpData)
        break
      case 'visits':
        tmpVisits = JSON.parse(JSON.stringify(visits))
        tmpVisits[index][property] = value
        setVisits(tmpVisits)
        break
      default:
        break
    }
  }

  const handleRemoveTracker = (index, key) => {
    let tmpData = []
    let tmpVisits = []

    switch (key) {
      case 'data':
        tmpData = JSON.parse(JSON.stringify(data))
        tmpData.splice(index, 1)
        setData(tmpData)
        break
      case 'visits':
        tmpVisits = JSON.parse(JSON.stringify(visits))
        tmpVisits.splice(index, 1)
        setVisits(tmpVisits)
        break
      default:
        break
    }
  }

  const checkGeneralStatus = () => {
    return !!general.name
  }

  const handleFinalizePlan = async () => {
    // Create payload object with all preventive plan data
    const payload = {
      name: general.name,
      description: general.description,
      tracking: [...data, ...visits],
      immunizations,
      lifestyle,
      riskFactors,
      education,
      planType: 'preventive'
    }

    // Validate required fields
    if (!payload.tracking || payload.tracking.length === 0) {
      return message.error('Please provide at least 1 diagnostic tracker for your preventive plan.')
    }

    if (isNew) {
      try {
        await createCarePlanTemplate(payload)
        message.success('Preventive care plan created successfully.')
        closeTab()
        refreshView()
      } catch (error) {
        message.error(handleError(error))
      }
    } else {
      await handleUpdatePlan(payload)
    }
  }

  const handleUpdatePlan = async (payload) => {
    try {
      const updatedPayload = {
        ...recordData,
        ...payload
      }

      await updateCarePlanTemplate(updatedPayload)
      message.success('Preventive care plan updated successfully.')
      closeTab()
      refreshView()
    } catch (error) {
      message.error(handleError(error))
    }
  }

  const closeTab = () => {
    dispatch(coreReducer.actions.closeTab(isNew ? 'PREVENTIVE_PLANS_new' : `PREVENTIVE_PLANS_${recordData._id}`))
  }

  // Define steps for the preventive plan wizard
  const steps = [
    {
      title: 'General',
      status: checkGeneralStatus() ? 'finish' : 'error',
      content: (
        <GeneralForm
          isMonitoring={false}
          data={general}
          setData={setGeneral}
          planType={planType}
          planLabel={planLabel}
        />
      )
    },
    {
      title: 'Visit Trackers',
      status: visits.length > 0 ? 'finish' : 'wait',
      content: (
        <VisitTrackers
          handleAddTracking={handleAddTracking}
          visits={visits}
          handleUpdateTracker={handleUpdateTracker}
          handleRemoveTracker={handleRemoveTracker}
          planType={planType}
          planLabel={planLabel}
        />
      )
    },
    {
      title: 'Diagnostic Tests',
      status: data.length > 0 ? 'finish' : 'wait',
      content: (
        <DataTrackerForm
          handleAddTracking={handleAddTracking}
          handleUpdateTracker={handleUpdateTracker}
          dataPoints={dataPoints}
          data={data}
          handleRemoveTracker={handleRemoveTracker}
          planType={planType}
          planLabel={planLabel}
          trackerType='diagnostic'
        />
      )
    },
    {
      title: 'Immunizations',
      status: immunizations.length > 0 ? 'finish' : 'wait',
      content: <ImmunizationsManagement immunizations={immunizations} setImmunizations={setImmunizations} />
    },
    {
      title: 'Lifestyle',
      status: lifestyle.length > 0 ? 'finish' : 'wait',
      content: <LifestyleRecommendations lifestyle={lifestyle} setLifestyle={setLifestyle} />
    },
    {
      title: 'Risk Factors',
      status: riskFactors.length > 0 ? 'finish' : 'wait',
      content: <RiskFactorAssessment riskFactors={riskFactors} setRiskFactors={setRiskFactors} />
    },
    {
      title: 'Education',
      status: education.length > 0 ? 'finish' : 'wait',
      content: <EducationalResources education={education} setEducation={setEducation} />
    },
    {
      title: 'Summary',
      status: 'wait',
      content: (
        <Summary
          general={general}
          data={data}
          visits={visits}
          isMonitoring={false}
          setCurrentStep={setCurrentStep}
          planType={planType}
          planLabel={planLabel}
          generalData={{
            name: general.name || '',
            description: general.description || ''
          }}
          trackersData={data || []}
          visitsData={visits || []}
        />
      )
    }
  ]

  return (
    <div className='preventive-plan-container'>
      <CustomRow>
        <Col span={24}>
          <Card
            size='small'
            style={{ marginBottom: 12 }}
            title={
              <Space>
                <span>{isNew ? `Create New ${planLabel}` : `Edit ${planLabel}`}</span>
                <Button
                  danger
                  onClick={() => {
                    closeTab()
                  }}
                >
                  <FontAwesomeIcon icon={faXmarkCircle} />
                </Button>
              </Space>
            }
          >
            <Steps
              current={currentStep}
              onChange={(step) => setCurrentStep(step)}
              items={steps.map((step) => ({
                title: step.title,
                status: step.status
              }))}
            />
          </Card>
        </Col>
        <Col span={24}>
          <Card
            size='small'
            extra={
              <Space>
                {currentStep > 0 && (
                  <Button type='primary' onClick={() => setCurrentStep(currentStep - 1)} style={{ marginRight: 8 }}>
                    <FontAwesomeIcon icon={faChevronLeft} />
                    Previous
                  </Button>
                )}
                {currentStep < steps.length - 1 ? (
                  <Button
                    disabled={currentStep === 0 && !checkGeneralStatus()}
                    type='primary'
                    onClick={() => setCurrentStep(currentStep + 1)}
                  >
                    Next
                    <FontAwesomeIcon icon={faChevronRight} />
                  </Button>
                ) : (
                  <Button type='primary' style={{ background: token.colorSuccess }} onClick={handleFinalizePlan}>
                    Finalize
                    <FontAwesomeIcon icon={faCheckCircle} />
                  </Button>
                )}
              </Space>
            }
          >
            <div className='step-content'>{steps[currentStep].content}</div>
          </Card>
        </Col>
      </CustomRow>
    </div>
  )
}

export default PlanTemplateForm
